/**
 * DiagnosticaGatePage — Fase 2 A7: pannello "simula ruolo"
 *
 * Incrocia gateRegistry.ts con la matrice permessi del DB e mostra,
 * per ogni ruolo, cosa è visibile, raggiungibile, precluso o senza gate.
 *
 * Strumento diagnostico — non è nel menu. Si raggiunge via URL:
 *   /diagnostica-gate   (richiede system.manage_roles)
 */

import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Card,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { gateRegistry, type GateEntry } from '@/config/gateRegistry'
import { usePageTitle } from '@/contexts/PageTitleContext'

const { Title, Text } = Typography

// ─── Tipi locali (rispecchiano la risposta di GET /permissions/matrix) ────────

interface RoleInfo {
  id: number
  codice: string
  nome: string
}

interface PermInfo {
  codice: string
  roles: { ruolo_codice: string; granted: boolean }[]
}

interface MatrixData {
  roles: RoleInfo[]
  permissions: PermInfo[]
}

// ─── Sezioni diagnostiche ─────────────────────────────────────────────────────

interface Sections {
  visible: GateEntry[]         // menu:true + permesso concesso
  reachable: GateEntry[]       // permesso concesso ma menu:false (escluse inSviluppo)
  precluded: GateEntry[]       // permesso non concesso (escluse inSviluppo)
  senzaGate: GateEntry[]       // permission:null (escluse pubblica:true)
  inSviluppo: GateEntry[]      // menu:false con motivoNoMenu contenente "sviluppo"
}

// ─── Helper: entry con funzione non ancora implementata ──────────────────────

function isInSviluppo(entry: GateEntry): boolean {
  return (
    !entry.menu &&
    entry.motivoNoMenu !== null &&
    entry.motivoNoMenu.toLowerCase().includes('sviluppo')
  )
}

// ─── Helper: controlla se un ruolo ha almeno uno dei permessi (OR) ────────────

function roleHasPermission(
  perm: string | string[] | null,
  grantedSet: Set<string>,
): boolean {
  if (perm === null) return false
  const arr = Array.isArray(perm) ? perm : [perm]
  return arr.some(p => grantedSet.has(p))
}

// ─── Colonne condivise ────────────────────────────────────────────────────────

function permissionCell(perm: string | string[] | null, record: GateEntry) {
  const perms = perm === null ? [] : Array.isArray(perm) ? perm : [perm]
  return (
    <Space direction="vertical" size={2}>
      <Space wrap size={4}>
        {perms.length === 0 ? (
          <Text type="secondary">—</Text>
        ) : (
          perms.map((p, i) => (
            <React.Fragment key={p}>
              <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {p}
              </Tag>
              {i < perms.length - 1 && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  OR
                </Text>
              )}
            </React.Fragment>
          ))
        )}
      </Space>
      {record.gateInterni && record.gateInterni.length > 0 && (
        <Tooltip
          title={`Gate interni alla pagina: ${record.gateInterni.join(', ')}`}
        >
          <Tag
            icon={<InfoCircleOutlined />}
            color="orange"
            style={{ fontSize: 11, cursor: 'help' }}
          >
            gate interni
          </Tag>
        </Tooltip>
      )}
    </Space>
  )
}

const baseColumns: ColumnsType<GateEntry> = [
  {
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
    width: 240,
    render: (v: string) => (
      <Text code style={{ fontSize: 12 }}>
        {v}
      </Text>
    ),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    key: 'label',
    width: 200,
  },
  {
    title: 'Permesso richiesto',
    key: 'permission',
    width: 300,
    render: (_: unknown, record: GateEntry) =>
      permissionCell(record.permission, record),
  },
  {
    title: 'Gruppo',
    dataIndex: 'gruppo',
    key: 'gruppo',
    width: 160,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
]

const senzaGateColumns: ColumnsType<GateEntry> = [
  {
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
    width: 220,
    render: (v: string) => (
      <Text code style={{ fontSize: 12 }}>
        {v}
      </Text>
    ),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    key: 'label',
    width: 160,
  },
  {
    title: 'Motivazione',
    dataIndex: 'senzaGateVoluto',
    key: 'senzaGateVoluto',
    render: (v: string | null, record: GateEntry) => (
      <Space direction="vertical" size={2}>
        <Text style={{ fontSize: 12 }}>{v ?? '—'}</Text>
        {record.gateInterni && record.gateInterni.length > 0 && (
          <Tooltip
            title={`Gate interni alla pagina: ${record.gateInterni.join(', ')}`}
          >
            <Tag
              icon={<InfoCircleOutlined />}
              color="orange"
              style={{ fontSize: 11, cursor: 'help' }}
            >
              gate interni
            </Tag>
          </Tooltip>
        )}
      </Space>
    ),
  },
  {
    title: 'Gruppo',
    dataIndex: 'gruppo',
    key: 'gruppo',
    width: 140,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
]

const inSviluppoColumns: ColumnsType<GateEntry> = [
  {
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
    width: 220,
    render: (v: string) => (
      <Text code style={{ fontSize: 12 }}>
        {v}
      </Text>
    ),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    key: 'label',
    width: 180,
  },
  {
    title: 'Permesso richiesto',
    key: 'permission',
    width: 260,
    render: (_: unknown, record: GateEntry) =>
      permissionCell(record.permission, record),
  },
  {
    title: 'Motivazione',
    dataIndex: 'motivoNoMenu',
    key: 'motivoNoMenu',
    render: (v: string | null) => (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {v ?? '—'}
      </Text>
    ),
  },
]

// ─── Componente sezione ───────────────────────────────────────────────────────

interface SectionProps {
  title: string
  badgeColor: string
  data: GateEntry[]
  columns: ColumnsType<GateEntry>
  emptyText?: string
}

const DiagSection: React.FC<SectionProps> = ({
  title,
  badgeColor,
  data,
  columns,
  emptyText = 'Nessuna rotta in questa categoria.',
}) => (
  <Card
    size="small"
    title={
      <Space>
        <Tag color={badgeColor} style={{ minWidth: 28, textAlign: 'center' }}>
          {data.length}
        </Tag>
        <Text strong>{title}</Text>
      </Space>
    }
    style={{ marginBottom: 16 }}
  >
    {data.length === 0 ? (
      <Text type="secondary">{emptyText}</Text>
    ) : (
      <Table<GateEntry>
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="path"
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    )}
  </Card>
)

// ─── Pagina principale ────────────────────────────────────────────────────────

const DiagnosticaGatePage: React.FC = () => {
  const { setTitle } = usePageTitle()

  const [matrix, setMatrix] = useState<MatrixData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)

  useEffect(() => {
    setTitle('Diagnostica Gate')
  }, [setTitle])

  useEffect(() => {
    axiosInstance
      .get<MatrixData>('/permissions/matrix')
      .then(res => {
        setMatrix(res.data)
        // Pre-seleziona ADMIN come default
        const admin = res.data.roles.find(r => r.codice === 'ADMIN')
        if (admin) setSelectedRole(admin.codice)
      })
      .catch(() => setError('Impossibile caricare la matrice permessi.'))
      .finally(() => setLoading(false))
  }, [])

  // Set dei permessi concessi al ruolo selezionato
  const grantedSet = useMemo<Set<string>>(() => {
    if (!matrix || !selectedRole) return new Set()
    const s = new Set<string>()
    for (const perm of matrix.permissions) {
      const entry = perm.roles.find(r => r.ruolo_codice === selectedRole)
      if (entry?.granted) s.add(perm.codice)
    }
    return s
  }, [matrix, selectedRole])

  // Categorizzazione delle entry del registro
  const sections = useMemo<Sections>(() => {
    const visible: GateEntry[] = []
    const reachable: GateEntry[] = []
    const precluded: GateEntry[] = []
    const senzaGate: GateEntry[] = []
    const inSviluppo: GateEntry[] = []

    for (const entry of gateRegistry) {
      if (entry.pubblica) continue // rotte pubbliche: fuori dalla simulazione ruolo

      // Sezione 5 — funzioni non ancora implementate: estratte per prime,
      // NON compaiono nelle sezioni 1–4 anche se il permesso è concesso.
      if (isInSviluppo(entry)) {
        inSviluppo.push(entry)
        continue
      }

      if (entry.permission === null) {
        // senzaGateVoluto (rotte autenticate senza gate di permesso)
        senzaGate.push(entry)
      } else {
        const granted = roleHasPermission(entry.permission, grantedSet)
        if (granted) {
          if (entry.menu) {
            visible.push(entry)
          } else {
            reachable.push(entry)
          }
        } else {
          precluded.push(entry)
        }
      }
    }

    return { visible, reachable, precluded, senzaGate, inSviluppo }
  }, [grantedSet])

  const isAdmin = selectedRole === 'ADMIN'

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return <Alert type="error" message={error} />
  }

  const roleOptions = (matrix?.roles ?? []).map(r => ({
    value: r.codice,
    label: `${r.codice} — ${r.nome}`,
  }))

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          Diagnostica Gate — simula ruolo
        </Title>
        <Text type="secondary">
          Incrocia il registro delle rotte con la matrice permessi del DB.
          Strumento di validazione; non è nel menu.
        </Text>
      </div>

      {/* Selettore ruolo */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <Space align="center" wrap>
          <Text strong>Ruolo:</Text>
          <Select
            style={{ minWidth: 260 }}
            placeholder="Seleziona un ruolo…"
            options={roleOptions}
            value={selectedRole}
            onChange={setSelectedRole}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          {selectedRole && (
            <Text type="secondary">
              {grantedSet.size} permessi concessi dalla matrice DB
            </Text>
          )}
        </Space>
      </Card>

      {/* Avviso ADMIN bypass */}
      {isAdmin && (
        <Alert
          type="info"
          showIcon
          icon={<WarningOutlined />}
          message="ADMIN bypassa i controlli lato backend"
          description="ADMIN (ruolo_id=1) ignora require_permission() nel codice: accede a tutto indipendentemente dalla matrice DB. Le sezioni sotto mostrano i granted del DB per ADMIN, che possono non riflettere l'accesso reale."
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedRole && (
        <>
          {/* 1. VISIBILI */}
          <DiagSection
            title="1 — VISIBILI (menu: true + permesso concesso)"
            badgeColor="green"
            data={sections.visible}
            columns={baseColumns}
            emptyText="Nessuna rotta visibile nel menu per questo ruolo."
          />

          {/* 2. RAGGIUNGIBILI NON NEL MENU */}
          <DiagSection
            title="2 — RAGGIUNGIBILI MA NON NEL MENU (permesso concesso, menu: false)"
            badgeColor="geekblue"
            data={sections.reachable}
            columns={baseColumns}
            emptyText="Nessuna rotta raggiungibile fuori menu per questo ruolo."
          />

          {/* 3. PRECLUSE */}
          <DiagSection
            title="3 — PRECLUSE (permesso non concesso)"
            badgeColor="red"
            data={sections.precluded}
            columns={baseColumns}
            emptyText="Nessuna rotta preclusa — il ruolo ha accesso a tutto il perimetro protetto."
          />

          {/* 4. SENZA GATE */}
          <DiagSection
            title="4 — SENZA GATE (permission: null, autenticate)"
            badgeColor="orange"
            data={sections.senzaGate}
            columns={senzaGateColumns}
            emptyText="Nessuna rotta senza gate."
          />

          {/* 5. IN SVILUPPO */}
          <DiagSection
            title="5 — IN SVILUPPO (menu: false, funzione non ancora attiva)"
            badgeColor="purple"
            data={sections.inSviluppo}
            columns={inSviluppoColumns}
            emptyText="Nessuna funzione in sviluppo registrata."
          />
        </>
      )}

      {/* Avviso fisso in fondo */}
      <Alert
        type="warning"
        showIcon
        message="Solo permessi attivi"
        description="La matrice espone solo i permessi con attivo = true nel DB. I permessi disattivati non compaiono e non concedono nulla, anche se una RolePermission li assegna. Le rotte pubbliche (pubblica: true) sono escluse dalla simulazione: non richiedono autenticazione."
        style={{ marginTop: 8 }}
      />
    </div>
  )
}

export default DiagnosticaGatePage
